import type { InvitationDTO } from '@/types/invitation';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts';

import { invitationService } from '@/services';
import { Spinner } from '@/components/ui';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { CheckCircle } from 'lucide-react';
import { CenteredFullScreenLayout } from '@/components/layout';

function SharerCard({ name, image }: { name: string; image: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/50 p-3">
      <Avatar className="size-10 shrink-0">
        <AvatarImage src={image} alt={name} />
        <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
          {name[0]?.toUpperCase() || 'U'}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{name}</p>
      </div>
    </div>
  );
}

export default function InvitationPage() {
  const { user, isSignedIn } = useAuth();
  const { mixId = '', invitationId = '' } = useParams<{ mixId: string; invitationId: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const fetchInvitationData = async () => {
      try {
        setLoading(true);
        setError(null);
        const invitationData = await invitationService.getInvitation(mixId, invitationId);
        setInvitation(invitationData);
      } catch (err) {
        console.error('Error fetching invitation data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch invitation');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitationData();
  }, [mixId, invitationId, isSignedIn]);

  const handleAcceptInvitation = async () => {
    if (!invitation) return;
    try {
      setAccepting(true);
      const userName =
        (user?.user_metadata?.full_name as string) ||
        'User';
      const userImage = (user?.user_metadata?.avatar_url as string) || '';
      await invitationService.acceptInvitation(invitation.id, invitation.mixId, userName, userImage);
      navigate('/home');
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    navigate('/home');
  };

  const handleSignIn = () => {
    const redirectUrl = encodeURIComponent(window.location.pathname);
    navigate(`/signin?redirect_url=${redirectUrl}`);
  };

  if (loading) {
    return (
      <CenteredFullScreenLayout>
        <div className="flex justify-center py-12">
          <Spinner className="size-12" />
        </div>
      </CenteredFullScreenLayout>
    );
  }

  if (error) {
    return (
      <CenteredFullScreenLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle>Something went wrong</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Go Home
            </Button>
          </CardFooter>
        </Card>
      </CenteredFullScreenLayout>
    );
  }

  if (!invitation) {
    return (
      <CenteredFullScreenLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle>Invitation Not Found</CardTitle>
            <CardDescription>
              The invitation you're looking for doesn't exist or has expired.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Go Home
            </Button>
          </CardFooter>
        </Card>
      </CenteredFullScreenLayout>
    );
  }

  if (invitation.status === 'ACCEPTED') {
    return (
      <CenteredFullScreenLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle>{invitation.mixName}</CardTitle>
            <CardDescription>Mix invitation accepted</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SharerCard
              name={invitation.sharerName}
              image={invitation.sharerImage}
            />
            <div className="flex flex-col items-center gap-2 py-2">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <p className="text-sm text-muted-foreground">
                You're all set — this invitation has already been accepted.
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/home')} className="w-full">
              Go to Mixes
            </Button>
          </CardFooter>
        </Card>
      </CenteredFullScreenLayout>
    );
  }

  if (invitation.status === 'EXPIRED') {
    return (
      <CenteredFullScreenLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle>Invitation Expired</CardTitle>
            <CardDescription>
              This invitation to "{invitation.mixName}" has expired and can no longer be
              accepted.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Go Home
            </Button>
          </CardFooter>
        </Card>
      </CenteredFullScreenLayout>
    );
  }

  if (!invitation.ownership) {
    return (
      <CenteredFullScreenLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle>{invitation.mixName}</CardTitle>
            <CardDescription>
              {invitation.sharerName} wants you to collaborate on this mix.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SharerCard
              name={invitation.sharerName}
              image={invitation.sharerImage}
            />
            <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
              You need to be signed in to accept this invitation.
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={handleSignIn} className="w-full">
              Sign In or Up to Accept
            </Button>
          </CardFooter>
        </Card>
      </CenteredFullScreenLayout>
    );
  }

  const canAccept = !invitation.ownership.isOwner && !invitation.ownership.isCollaborator;

  if (!canAccept) {
    return (
      <CenteredFullScreenLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle>{invitation.mixName}</CardTitle>
            <CardDescription>You already have access to this mix.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-2">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate(`/mixes/${mixId}`)} className="w-full">
              Go to Mix
            </Button>
          </CardFooter>
        </Card>
      </CenteredFullScreenLayout>
    );
  }

  return (
    <CenteredFullScreenLayout>
      <Card>
        <CardHeader className="text-center">
          <CardTitle>{invitation.mixName}</CardTitle>
          <CardDescription>
            {invitation.sharerName} has invited you to collaborate on this mix.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <SharerCard
            name={invitation.sharerName}
            image={invitation.sharerImage}
          />
        </CardContent>
        <CardFooter className="gap-2">
          <Button variant="outline" onClick={handleDecline} className="flex-1">
            Decline
          </Button>
          <Button onClick={handleAcceptInvitation} disabled={accepting} className="flex-1">
            {accepting ? 'Accepting...' : 'Accept'}
          </Button>
        </CardFooter>
      </Card>
    </CenteredFullScreenLayout>
  );
}
